/**
 * All balance data for the game lives here: tank handling, shells, mines, the
 * camera rig, the enemy-tank roster, and how each endless layer scales. Tuning
 * the game = editing this file.
 *
 * Units are Roblox studs (1:1 with the imported .obj assets). A tank's tread
 * footprint is 20×20 studs, so a tank "radius" is ~10.
 */

export const PLAYER_COLOR = 0x2f6fe0; // the player drives the blue tank

// Global gameplay pace. <1 slows the whole simulation (tanks, shells, fire rates)
// uniformly — since human reaction time is fixed, this buys real time to plan
// shots and makes the game less of a frenzy. Camera + input stay crisp.
export const SIM_SPEED = 0.8;

// Bullet colour-coding: your shots are blue, incoming enemy shots are red.
export const SHELL_COLOR = { player: 0x40b4ff, enemy: 0xff4040, rocket: 0xff6a1a, ally: 0x46e6b4 };
/** Muzzle-flash colors for weapon types lacking a shell-color (others reuse SHELL_COLOR/ARC_COLOR/TOW_COLOR). */
export const MUZZLE = { gatling: 0xffd27a, sniper: 0xbfffff, mlrs: 0xff7a2a };

export const TANK = {
  radius: 8.5, // collision radius (footprint half-width is 10)
  moveSpeed: 46, // player drive speed (studs/s)
  turnSpeed: 5.5, // body-heading turn rate toward the move direction (rad/s lerp)
  turretTurn: 11, // turret aim slew (rad/s lerp)
};

export const BULLET = {
  speed: 118,
  radius: 2.2, // physics radius (wall bounce + shell-vs-shell + aim line)
  maxBounces: 1, // default ricochets before a shell fizzles
  life: 6, // seconds before a shell expires on its own
  cooldown: 0.62, // player fire cooldown (s)
  maxPlayerShells: 5, // concurrent live player shells (Wii Tanks caps at 5)
};

export const MINE = {
  radius: 44, // blast radius (studs) — a big, telegraphed boom (red danger ring beforehand)
  fuse: 5, // auto-detonate after this long
  armTime: 0.7, // owner is immune to its own mine until armed (short delay)
  maxPlayer: 2, // concurrent player mines
};

export const CAMERA = {
  height: 215, // studs above the focus point (at zoom 1)
  back: 130, // studs toward +Z (so the view tilts, looking "north")
  fov: 42,
  lerp: 6.5, // follow stiffness
  minZoom: 0.55,
  maxZoom: 4.5, // zoom out much farther for big late-game floors
  zoomStep: 0.1, // per wheel notch
  zoomKeyRate: 1.6, // zoom units/sec while holding I (in) / O (out)
  rotateSpeed: 2.0, // rad/s while holding Q/E
};

export interface TankType {
  id: string;
  name: string;
  color: number;
  /** does it drive around, or sit and turn its turret? */
  mobile: boolean;
  moveSpeed: number;
  fireInterval: number; // seconds between shots
  bulletSpeed: number;
  bounces: number; // shell ricochets
  maxShells: number; // concurrent shells this tank keeps in the air
  aimError: number; // radians of aim jitter (higher = sloppier)
  lead: number; // 0..1 how much it leads the player's velocity
  mines: boolean; // lays mines
  health: number; // hits to destroy
  /** fully invisible body/turret/barrel — only its treads (and tread trail) show */
  ghost?: boolean;
  /** fires fast, non-bouncing rockets instead of bouncing shells */
  rocket?: boolean;
  /** GATLING: hoses rockets extremely rapidly (a rocket type with a tiny fire interval) */
  gatling?: boolean;
  /** "god-like aim": computes perfect bank shots up to this many bounces */
  aimBounces?: number;
  /** charges the player at 2× speed and detonates like a mine on contact; no turret */
  kamikaze?: boolean;
  /** movement style for mobile tanks (default "chase"). kite = keep distance while
   *  keeping a shot; flee = stay away; shield-ally = move to the nearest ally */
  move?: "chase" | "kite" | "flee" | "highground" | "shield-ally";
  /** draws a laser sight tracing its firing solution (snipers) */
  laser?: boolean;
  /** turretless support: periodically spawns mobile tanks to fight the player */
  spawner?: boolean;
  /** support: runs to the nearest ally and grants/refreshes its shields */
  shieldGen?: boolean;
  /** stays back and THROWS nearby chase-type allies next to the player (no damage —
   *  a repositioning tool that shuffles where threats appear, incl. flanks) */
  thrower?: boolean;
  /** ARTILLERY: seeks high ground and lobs ARCING shells over walls at the player */
  artillery?: boolean;
  /** MLRS: a STATIONARY launcher — rapid, very inaccurate arc-shell SALVOS of a dozen at infinite range */
  mlrs?: boolean;
  /** TOW: fires slow HEAT-SEEKING missiles (that can be shot down) instead of shells */
  tow?: boolean;
  /** the ABOMINATION boss — very high HP, cycles every ability (arc/homing/spawn/shield/throw) */
  abomination?: boolean;
  /** LASER: a stationary turret that fires a CONTINUOUS beam (bounces twice) which burns
   *  the player's team; a player shell crossing the beam staggers it for a moment */
  laserBeam?: boolean;
  /** MECHA body MODIFIER: this instance rides a tall 4-legged walker (any turret on top).
   *  Strides over walls, crouches on line-of-sight to fire its turret (then the base is the
   *  weak point), and topples + explodes when 2 of its 4 ground legs are destroyed. */
  mecha?: boolean;
}

/** MECHA walker tuning (a body modifier any eligible type can wear; see prefab buildMecha). */
export const MECHA = {
  legHp: 3,         // hits to destroy one ground leg (only AFTER shields are gone)
  shields: 3,       // leg-shield charges (separate from base health) — leg hits DRAIN these before any leg pops
  legsToTopple: 2,  // legs lost before it falls over + explodes
  legHitR: 12,      // hit radius around each foot (raised: legs are the only target)
  baseHitR: 16,     // hit radius on the lowered base (crouched: the weak point)
  bodyR: 24,        // body collision radius vs the perimeter (it strides over walls)
  aoeR: 64,         // topple explosion radius
  speed: 75,        // striding roam speed (studs/s) — a fast giant
  crouchSpeed: 42,  // slower movement while crouched + firing
  limpMul: 0.55,    // speed multiplier after losing the 1st leg
  crouchTime: 0.15, // seconds to lower / raise — SNAPPY servo (crouched MOVE speed is crouchSpeed, separate)
  dwell: 2.6,       // seconds it stays crouched firing (the punish window)
  fleeTime: 1.1,    // seconds it uncrouches + flees after a shot threatens its base
  baseRest: 44,     // rest height of the base/turret (world units above the feet)
  maxRaise: 16,     // how far it can extend UP to shoot a tank on a road (legs straighten)
  fireRange: 620,   // range at which it stops to crouch + fire
  scale: 1.0,       // overall walker scale (model is authored at the intended size)
  unlockLayer: 30,  // LEGACY (unused): mecha spawn schedule now lives in mechaPlan() — debut wave 10/14, ramps to all by 100
};
/** Can this type wear the mecha body? Excludes the turretless suicide body (kamikaze), the
 *  invisible ghost (defeats the leg weak-points), the boss Abomination, the laser (its beam
 *  cycle isn't wired to the walker), and the plain merc. Everything else (shell tanks, snipers,
 *  rockets, artillery/mlrs, tow, AND the Hatchery/Aegis/Hurler support kits) is eligible. */
export function mechaEligible(t: TankType): boolean {
  return !(t.kamikaze || t.ghost || t.abomination || t.laserBeam || t.id === "ally");
}
/** Mecha spawn schedule for floor index `layerIdx` (0-based; displayed floor/wave = layerIdx + 1).
 *  Debut on wave 10, again on wave 14, then EVERY non-mono wave from 15 on has >=1 mecha, the
 *  per-eligible-slot fraction ramping to 1.0 (all eligible become walkers) by wave 100.
 *  Returns { forceOne: guarantee >=1 mecha this wave, p: per-eligible-slot probability }. */
export function mechaPlan(layerIdx: number): { forceOne: boolean; p: number } {
  const wave = layerIdx + 1;
  if (wave < 10) return { forceOne: false, p: 0 };                 // none before wave 10
  if (wave === 10 || wave === 14) return { forceOne: true, p: 0 }; // debut waves: exactly one
  if (wave < 14) return { forceOne: false, p: 0 };                 // waves 11-13: none
  if (wave >= 100) return { forceOne: true, p: 1 };                // wave 100+: ALL eligible
  return { forceOne: true, p: (wave - 14) / 86 };                  // waves 15-99: >=1, ramping to 1.0 at 100
}

/** The enemy roster, loosely faithful to the Wii Play Tanks colours. */
export const TANK_TYPES: Record<string, TankType> = {
  brown: { id: "brown", name: "Brown", color: 0x8a5a2b, mobile: false, moveSpeed: 0, fireInterval: 3.2, bulletSpeed: 70, bounces: 0, maxShells: 1, aimError: 0.10, lead: 0, mines: false, health: 1 },
  grey: { id: "grey", name: "Ash", color: 0x9aa0a6, mobile: true, moveSpeed: 22, fireInterval: 2.6, bulletSpeed: 70, bounces: 1, maxShells: 1, aimError: 0.14, lead: 0.2, mines: false, health: 1 },
  teal: { id: "teal", name: "Marine", color: 0x1f9e9e, mobile: false, moveSpeed: 0, fireInterval: 1.7, bulletSpeed: 130, bounces: 1, maxShells: 2, aimError: 0.05, lead: 0.4, mines: false, health: 1 },
  yellow: { id: "yellow", name: "Yellow", color: 0xd8c020, mobile: true, moveSpeed: 30, fireInterval: 2.2, bulletSpeed: 80, bounces: 1, maxShells: 1, aimError: 0.10, lead: 0.3, mines: true, health: 1 },
  red: { id: "red", name: "Red", color: 0xc83232, mobile: true, moveSpeed: 40, fireInterval: 1.6, bulletSpeed: 95, bounces: 1, maxShells: 2, aimError: 0.07, lead: 0.45, mines: false, health: 1 },
  green: { id: "green", name: "Forest", color: 0x2f9e3a, mobile: false, moveSpeed: 0, fireInterval: 2.4, bulletSpeed: 105, bounces: 2, maxShells: 2, aimError: 0.02, lead: 0.5, mines: false, health: 1 },
  purple: { id: "purple", name: "Violet", color: 0x8a3fcf, mobile: true, moveSpeed: 44, fireInterval: 1.8, bulletSpeed: 95, bounces: 1, maxShells: 2, aimError: 0.07, lead: 0.5, mines: true, health: 1 },
  white: { id: "white", name: "Ghost", color: 0xeafaff, mobile: true, moveSpeed: 36, fireInterval: 2.0, bulletSpeed: 90, bounces: 1, maxShells: 2, aimError: 0.08, lead: 0.4, mines: true, health: 1, ghost: true },
  black: { id: "black", name: "Black", color: 0x222222, mobile: true, moveSpeed: 56, fireInterval: 1.3, bulletSpeed: 120, bounces: 1, maxShells: 3, aimError: 0.05, lead: 0.6, mines: true, health: 2 },
  // rocketeers: extremely fast, non-bouncing rockets (Wii Play Tanks rockets)
  rocket: { id: "rocket", name: "Rocket", color: 0xff7a00, mobile: false, moveSpeed: 0, fireInterval: 2.8, bulletSpeed: 280, bounces: 0, maxShells: 1, aimError: 0.06, lead: 0.35, mines: false, health: 1, rocket: true },
  marauder: { id: "marauder", name: "Marauder", color: 0xe04010, mobile: true, moveSpeed: 34, fireInterval: 2.0, bulletSpeed: 320, bounces: 0, maxShells: 2, aimError: 0.05, lead: 0.55, mines: false, health: 2, rocket: true },
  // gatling: HOSES a rapid stream of non-bouncing rockets — slow, tanky, sprays a dodgeable cone
  gatling: { id: "gatling", name: "Gatling", color: 0x4f5a66, mobile: true, moveSpeed: 24, fireInterval: 0.16, bulletSpeed: 290, bounces: 0, maxShells: 6, aimError: 0.12, lead: 0.45, mines: false, health: 3, rocket: true, gatling: true },
  // sniper: god-like aim — perfect bank shots up to 5 bounces (long-lived shells)
  sniper: { id: "sniper", name: "Sniper", color: 0x14110f, mobile: false, moveSpeed: 0, fireInterval: 3.2, bulletSpeed: 1000, bounces: 5, maxShells: 1, aimError: 0.0, lead: 0.3, mines: false, health: 1, aimBounces: 5, laser: true },
  // brute: a damage sponge — 3 shields (4 HP), otherwise an ordinary brawler
  brute: { id: "brute", name: "Brute", color: 0x7a5cc0, mobile: true, moveSpeed: 26, fireInterval: 1.9, bulletSpeed: 95, bounces: 1, maxShells: 2, aimError: 0.09, lead: 0.4, mines: false, health: 4 },
  // kamikaze: a turretless mobile mine — sprints at the player at 2× speed and detonates on contact
  kamikaze: { id: "kamikaze", name: "Kamikaze", color: 0xff2e2e, mobile: true, moveSpeed: 92, fireInterval: 99, bulletSpeed: 0, bounces: 0, maxShells: 0, aimError: 0, lead: 0, mines: false, health: 1, kamikaze: true },
  // mobile sniper: god-aim that KITES — keeps its distance while holding a valid bank shot
  stalker: { id: "stalker", name: "Stalker", color: 0x2b2f4a, mobile: true, moveSpeed: 32, fireInterval: 2.6, bulletSpeed: 1000, bounces: 5, maxShells: 1, aimError: 0.0, lead: 0.3, mines: false, health: 1, aimBounces: 5, laser: true, move: "kite" },
  // spawner: turretless, flees, and conjures mobile tanks to swarm the player
  spawner: { id: "spawner", name: "Hatchery", color: 0x3f9d63, mobile: true, moveSpeed: 36, fireInterval: 99, bulletSpeed: 0, bounces: 0, maxShells: 0, aimError: 0, lead: 0, mines: false, health: 3, spawner: true, move: "flee" },
  // shield generator: runs to the nearest ally and keeps it shielded
  shieldgen: { id: "shieldgen", name: "Aegis", color: 0x32d6c4, mobile: true, moveSpeed: 50, fireInterval: 99, bulletSpeed: 0, bounces: 0, maxShells: 0, aimError: 0, lead: 0, mines: false, health: 3, shieldGen: true, move: "shield-ally" },
  // thrower: stays back and tosses chase-type allies next to the player (no damage)
  thrower: { id: "thrower", name: "Hurler", color: 0xb8702e, mobile: true, moveSpeed: 28, fireInterval: 99, bulletSpeed: 0, bounces: 0, maxShells: 0, aimError: 0, lead: 0, mines: false, health: 3, thrower: true, move: "flee" },
  // artillery: climbs toward high ground and lobs arcing shells over the walls
  artillery: { id: "artillery", name: "Howitzer", color: 0xcf7a2a, mobile: true, moveSpeed: 24, fireInterval: 3.6, bulletSpeed: 0, bounces: 0, maxShells: 1, aimError: 0, lead: 0.6, mines: false, health: 2, artillery: true, move: "highground" },
  // MLRS: a STATIONARY launcher — fires rapid, wildly inaccurate salvos of a dozen arc shells at infinite range
  mlrs: { id: "mlrs", name: "MLRS", color: 0x6e7a3a, mobile: false, moveSpeed: 0, fireInterval: 99, bulletSpeed: 0, bounces: 0, maxShells: 0, aimError: 0, lead: 0, mines: false, health: 4, mlrs: true },
  // TOW: kites + launches slow heat-seeking missiles you can shoot out of the air
  tow: { id: "tow", name: "TOW", color: 0x7a4fb0, mobile: true, moveSpeed: 30, fireInterval: 3.4, bulletSpeed: 0, bounces: 0, maxShells: 2, aimError: 0, lead: 0, mines: false, health: 2, tow: true, move: "kite" },
  // ABOMINATION: a rare boss with all the toys — fat HP, shells, and a cycling grab-bag of abilities
  abomination: { id: "abomination", name: "ABOMINATION", color: 0x8b0000, mobile: true, moveSpeed: 30, fireInterval: 1.6, bulletSpeed: 110, bounces: 1, maxShells: 2, aimError: 0.06, lead: 0.5, mines: true, health: 12, abomination: true },
  // LASER: a stationary emitter — sweeps a continuous twice-bouncing beam that burns you;
  // shoot the beam to stagger it. A shield (2 HP) so it isn't trivially one-shot.
  laserbeam: { id: "laserbeam", name: "Laser", color: 0xff3df0, mobile: false, moveSpeed: 0, fireInterval: 99, bulletSpeed: 0, bounces: 0, maxShells: 0, aimError: 0, lead: 0, mines: false, health: 2, laserBeam: true },
  // MERCENARY: a friendly ally tank you HIRE in the round-end shop. Fights on your
  // side (hunts enemies, never the player), and follows you between floors. Base health
  // 1 — its only shields are the player's Bulwark count, granted in spawnAlly (so a merc
  // is exactly as durable as the player, never more).
  ally: { id: "ally", name: "Mercenary", color: 0x2fd0c0, mobile: true, moveSpeed: 46, fireInterval: 0.9, bulletSpeed: 128, bounces: 1, maxShells: 3, aimError: 0.03, lead: 0.4, mines: false, health: 1 },
};

/**
 * Which enemies appear on a given layer (0-indexed). Endless: the count climbs
 * and tougher types get mixed in, then small health bumps keep scaling forever.
 */
export function enemiesForLayer(layer: number): TankType[] {
  // a WEIGHTED pool (repeats = weight). Mobile threats crowd out the stationary
  // "sitting ducks" as you climb — non-sniper stationary tanks (brown/teal/green/
  // rocket) drop to a low weight in later floors so they become uncommon.
  const pool: string[] = [];
  const add = (id: string, w: number) => { for (let k = 0; k < w; k++) if (w > 0) pool.push(id); };
  // weight for non-sniper STATIONARY "sitting duck" tanks (brown/teal/green/rocket):
  // common early, tapering to ZERO by ~layer 50 so late floors aren't a chore of
  // hunting passive corner-campers. (The sniper is an ACTIVE threat — exempt below.)
  const duck = Math.max(0, 3 - Math.floor(layer / 16));

  add("brown", layer < 20 ? duck : 0);          // stationary
  add("grey", 2);                                // mobile
  if (layer >= 2) add("teal", duck);             // stationary
  if (layer >= 3) add("yellow", 3);              // mobile, mines
  if (layer >= 5) { add("red", 3); add("green", duck); } // green is stationary
  if (layer >= 8) { add("purple", 3); add("white", 3); }
  if (layer >= 10) add("brute", 2);              // mobile damage-sponge
  if (layer >= 12) { add("black", 3); add("kamikaze", 3); }
  if (layer >= 14) add("sniper", 2);             // stationary but a real threat — exempt from the duck nerf
  if (layer >= 16) add("rocket", duck);          // stationary rocketeer
  if (layer >= 16) { add("thrower", 1); add("artillery", 1); } // support + arcing mortar
  if (layer >= 18) { add("stalker", 3); add("spawner", 1); }    // mobile sniper + spawner
  if (layer >= 20) { add("shieldgen", 1); add("tow", 2); }      // shields + homing missiles
  if (layer >= 22) add("marauder", 3);           // mobile rocketeer
  if (layer >= 24) add("laserbeam", 2);          // stationary continuous-beam emitter
  if (layer >= 26) add("gatling", 2);            // mobile rapid-fire rocket hose
  if (layer >= 27) add("mlrs", 1);               // stationary salvo launcher (infinite range)
  if (layer >= 28) add("abomination", 1);        // rare boss — all abilities combined

  // the TOTAL roster per floor keeps growing forever (no ceiling) — the EnemyManager
  // gates how many are alive on the field at once (`fieldLimit`) and spawns the rest
  // as the player/mercs whittle them down. So deep floors are a real grind, not a wall.
  const count = 2 + Math.floor(layer * 0.85);
  // a tiny deterministic-ish jitter from the layer number keeps each layer's mix
  // distinct without needing Math.random at construction time
  const out: TankType[] = [];
  for (let i = 0; i < count; i++) {
    const pick = pool[(i * 7 + layer * 3 + 1) % pool.length];
    const base = TANK_TYPES[pick];
    // +1 health on every enemy each 14 layers (endless scaling) — but ghosts stay
    // stealthy (no shield badges).
    let bonusHp = base.ghost || base.kamikaze ? 0 : Math.floor(layer / 14);
    // kamikazes used to stay one-shot; from floor 20 they gain a shield, +1 per 10 floors.
    // (`layer` here is the 0-based floor index, so displayed floor 20 = index 19.)
    if (base.kamikaze && layer >= 19) bonusHp = Math.floor((layer - 19) / 10) + 1;
    out.push(bonusHp > 0 ? { ...base, health: base.health + bonusHp } : base);
  }
  return out;
}

/** Max enemies ALIVE on the field at once (the roster total is unbounded — this gates
 *  the drip-feed so the floor stays a fight, not a swarm). */
export function enemyFieldLimit(layer: number): number {
  return Math.min(2 + Math.floor(layer * 0.85), 16);
}

/** The 0-based layer index each enemy type first appears on (displayed floor = +1).
 *  ⚠️ Keep in sync with the `if (layer >= N)` gates in enemiesForLayer above — this is
 *  the single source the field guide reads for "first appears on floor N". */
export const UNLOCK_LAYER: Record<string, number> = {
  brown: 0, grey: 0, teal: 2, yellow: 3, red: 5, green: 5, purple: 8, white: 8,
  brute: 10, black: 12, kamikaze: 12, sniper: 14, rocket: 16, thrower: 16, artillery: 16,
  stalker: 18, spawner: 18, shieldgen: 20, tow: 20, marauder: 22, laserbeam: 24,
  gatling: 26, mlrs: 27, abomination: 28,
};

/** Types that can't carry a mono-type round on their own — pure support (no attack of
 *  their own: Aegis shields, the Hatchery/Hurler only conjure or reposition allies) and
 *  the one-off ABOMINATION boss (a whole field of bosses would be an unfair wall). */
const MONO_EXCLUDE = new Set(["shieldgen", "spawner", "thrower", "abomination"]);

/** Every 3rd DISPLAYED floor (3, 6, 9, ... → 0-based index 2, 5, 8, ...) is a special
 *  MONO-TYPE round: the whole field is a SINGLE attack-capable tank type. Returns that
 *  one-type roster (same size + health-scaling as the normal floor), or null on a normal
 *  floor. The type is drawn from this floor's natural roster and BIASED toward the rarer
 *  (i.e. just-unlocked / harder) types, so the mono-type scales with difficulty while
 *  still varying floor to floor. */
export function monoRosterForLayer(layer: number): TankType[] | null {
  if ((layer + 1) % 3 !== 0) return null;
  const roster = enemiesForLayer(layer);
  // tally each attack-capable type's natural weight on this floor — rarer = harder/newer
  const counts = new Map<string, number>();
  for (const t of roster) if (!MONO_EXCLUDE.has(t.id)) counts.set(t.id, (counts.get(t.id) ?? 0) + 1);
  if (counts.size === 0) return null; // (never happens — grey is always present)
  const cand = [...counts.keys()].sort((a, b) => counts.get(a)! - counts.get(b)!); // hardest (rarest) first
  const pool = cand.slice(0, Math.max(1, Math.round(cand.length * 0.6))); // the harder ~60%
  const id = pool[(layer * 5 + 2) % pool.length]; // deterministic per floor, but cycles for variety
  const base = TANK_TYPES[id];
  let bonusHp = base.ghost || base.kamikaze ? 0 : Math.floor(layer / 14); // same endless scaling as the normal roster
  if (base.kamikaze && layer >= 19) bonusHp = Math.floor((layer - 19) / 10) + 1;
  const scaled = bonusHp > 0 ? { ...base, health: base.health + bonusHp } : base;
  return new Array(roster.length).fill(scaled);
}

export const LIVES_START = 3;

// ---- CARD MODE -------------------------------------------------------------
// A deckbuilder spin on the climb: no player tank — you spend cash to deploy ALLY
// tanks from an evolving hand onto your half of the grid, clear each floor's enemy
// roster, and climb. (Economy + evolving hand ported from the bskyrts card game.)
export const CARD_MODE = {
  startCash: 170,   // affords ~3 cheap tanks to open
  killReward: 41,   // cash per enemy destroyed (dialed in via the dev panel)
  coreSlots: 3,     // cards in hand
  maxLevel: 6,      // upgrade-level cap a card can drift to
};
/** Ally tank types offered as cards (v1 = the shell-firing types the merc AI fields well;
 *  the special-weapon types — artillery/laser/mlrs/tow/kamikaze/support — come once the
 *  ally AI can express their weapons). */
export const CARD_TYPES: string[] = [
  "brown", "grey", "teal", "yellow", "red", "green", "purple", "black",
  "brute", "rocket", "marauder", "gatling", "sniper", "stalker",
  // v2 combat specials (their real AI as allies):
  "artillery", "mlrs", "tow", "laserbeam", "kamikaze",
  // v2 summoners / support (team-aware summon / shield / throw kits):
  "spawner", "shieldgen", "thrower", "abomination",
];
/** Base deploy cost per type (before level + price-roll + per-type field escalation). */
export const CARD_BASE_COST: Record<string, number> = {
  brown: 30, grey: 35, teal: 45, yellow: 50, red: 55, green: 65, purple: 65, black: 85,
  brute: 75, rocket: 60, marauder: 90, gatling: 115, sniper: 95, stalker: 100,
  artillery: 95, mlrs: 135, tow: 105, laserbeam: 125, kamikaze: 35,
  spawner: 140, shieldgen: 90, thrower: 120, abomination: 180,
};
